/** Lists that track a Resume Row. Keyed so each list resumes independently. */
export type ResumeRowList = 'transactions' | 'reports';

const resumeRows: Partial<Record<ResumeRowList, string>> = {};

/**
 * Record the row a list should re-open on. Set both when the user opens a row's
 * detail screen and when they page through the list by hand, so it always names
 * the row they were last looking at.
 *
 * Held in memory only: the Resume Row is a within-session convenience, so a full
 * page reload deliberately starts back at the first page.
 */
export function setResumeRow(list: ResumeRowList, rowKey: string | undefined): void {
  if (rowKey === undefined) {
    delete resumeRows[list];
    return;
  }
  resumeRows[list] = rowKey;
}

/** The row a list should re-open on, if one has been recorded this session. */
export function getResumeRow(list: ResumeRowList): string | undefined {
  return resumeRows[list];
}

/**
 * The page holding the Resume Row, given the list's current sorted and filtered
 * order. Returns undefined when there is nothing to resume to — no Resume Row,
 * an empty list, or a row that has since been deleted or filtered away — leaving
 * the caller on the first page.
 */
export function resolveResumeRowPage(
  resumeRowKey: string | undefined,
  orderedRowKeys: string[],
  pageSize: number,
): number | undefined {
  if (!resumeRowKey || pageSize <= 0) {
    return undefined;
  }
  const index = orderedRowKeys.indexOf(resumeRowKey);
  if (index < 0) {
    return undefined;
  }
  return Math.floor(index / pageSize);
}
